import { Mail, Sparkles, Check, ArrowRight, Users, Phone, Building2 } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const steps = [
  {
    n: "01",
    title: "Open the email",
    body: "Read any email in Outlook. Email2Contact appears in the sidebar.",
    visual: <EmailVisual />,
  },
  {
    n: "02",
    title: "AI extracts the signature",
    body: "Fields are parsed in seconds. Edit anything before saving.",
    visual: <ExtractVisual />,
  },
  {
    n: "03",
    title: "Save to Outlook",
    body: "Contact lands in your Microsoft account. No copy-paste, no duplicates.",
    visual: <SaveVisual />,
  },
];

export const StepStoryboard = () => {
  return (
    <div className="grid lg:grid-cols-3 gap-6 lg:gap-4 relative">
      {/* Connecting line — desktop */}
      <div className="hidden lg:block absolute top-[88px] left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      {steps.map((step, i) => (
        <Step key={i} step={step} index={i} />
      ))}
    </div>
  );
};

const Step = ({ step, index }: { step: typeof steps[0]; index: number }) => {
  const { ref, animationClass } = useScrollAnimation<HTMLDivElement>({
    type: "slide-up",
    delay: index * 120,
    threshold: 0.15,
  });

  return (
    <div ref={ref} className={`relative ${animationClass}`}>
      <div className="fluent-tile fluent-tile-hover p-5 lg:p-6 h-full bg-card flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono text-[11px] tracking-widest text-muted-foreground">
            STEP / {step.n}
          </span>
          <div className="flex-1 h-px bg-border" />
          <div className="w-7 h-7 rounded-full bg-surface-2 border border-border grid place-items-center text-xs font-semibold text-foreground">
            {index + 1}
          </div>
        </div>

        {/* Mini visual */}
        <div className="mb-5 rounded-lg bg-surface-1 border border-border/60 p-3 h-44 grid place-items-center overflow-hidden">
          {step.visual}
        </div>

        <h3 className="text-lg font-display font-semibold mb-1.5">{step.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
      </div>

      {/* Arrow between steps (desktop) */}
      {index < 2 && (
        <ArrowRight className="hidden lg:block absolute -right-3 top-[80px] w-5 h-5 text-border bg-background z-10" />
      )}
    </div>
  );
};

function EmailVisual() {
  return (
    <div className="w-full space-y-1.5 text-left">
      <div className="flex items-center gap-1.5 text-[9px] font-mono text-muted-foreground uppercase tracking-wider">
        <Mail className="w-3 h-3" /> Inbox · 1 unread
      </div>
      <div className="rounded bg-card border border-border p-2.5 shadow-fluent-2">
        <div className="text-[11px] font-semibold truncate">Re: Partnership proposal</div>
        <div className="text-[9px] text-muted-foreground">Sarah Mitchell · 10:24 AM</div>
        <div className="mt-1.5 h-1 bg-surface-2 rounded w-full" />
        <div className="mt-1 h-1 bg-surface-2 rounded w-4/5" />
        <div className="mt-1 h-1 bg-surface-2 rounded w-3/5" />
        <div className="mt-2 px-1.5 py-1 rounded border border-dashed border-primary/40 bg-primary/[0.04]">
          <div className="text-[9px] font-semibold">Sarah Mitchell</div>
          <div className="text-[8px] text-muted-foreground">VP Ops · Northwind Labs</div>
        </div>
      </div>
    </div>
  );
}

function ExtractVisual() {
  const fields = [
    { Icon: Users, label: "NAME", value: "Sarah Mitchell" },
    { Icon: Building2, label: "COMPANY", value: "Northwind Labs" },
    { Icon: Phone, label: "PHONE", value: "+1 415 555 0142" },
  ];
  return (
    <div className="w-full space-y-1.5">
      <div className="flex items-center gap-1.5 text-[9px] font-mono text-primary uppercase tracking-wider">
        <Sparkles className="w-3 h-3" /> Extracting fields…
      </div>
      {fields.map(({ Icon, label, value }, i) => (
        <div
          key={i}
          className="flex items-center gap-2 p-1.5 rounded bg-card border border-border/60 shadow-fluent-2"
        >
          <Icon className="w-3 h-3 text-muted-foreground" />
          <div className="flex-1 min-w-0">
            <div className="text-[8px] font-mono uppercase text-muted-foreground">{label}</div>
            <div className="text-[10px] font-medium truncate">{value}</div>
          </div>
          <Check className="w-2.5 h-2.5 text-success" />
        </div>
      ))}
    </div>
  );
}

function SaveVisual() {
  return (
    <div className="w-full flex flex-col items-center gap-2 py-2">
      <div className="w-12 h-12 rounded-full bg-success/10 border border-success/30 grid place-items-center">
        <Check className="w-6 h-6 text-success" />
      </div>
      <div className="text-[11px] font-semibold">Sarah Mitchell</div>
      <div className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">
        Saved → Outlook Contacts
      </div>
      <div className="flex gap-1 mt-1">
        <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-surface-2 text-foreground">
          partner
        </span>
        <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-surface-2 text-foreground">
          q2-deals
        </span>
      </div>
    </div>
  );
}
